import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { formatServerDateTime } from './outboxFormat';

/**
 * StatCard - número grande + rótulo. Quando `alert` está ligado o card fica
 * vermelho: é o caso de "Atrasadas", que só passa de zero com o despachante parado.
 */
function StatCard({ label, value, caption, alert = false, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid',
        borderColor: alert ? '#EF9A9A' : '#E8ECF0',
        bgcolor: alert ? '#FFF5F5' : '#fff',
        p: '16px 20px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s ease',
        '&:hover': onClick ? { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : undefined,
      }}
    >
      <Typography variant="body2" color={alert ? 'error.main' : 'text.secondary'} sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, fontSize: '1.75rem', lineHeight: 1, mt: 0.5, color: alert ? 'error.main' : 'text.primary' }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color={alert ? 'error.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
          {caption}
        </Typography>
      )}
    </Card>
  );
}

/**
 * OutboxSummary - Cards do topo da tela "Fila WhatsApp" (Admin Geral)
 *
 * O resumo é sempre da fila inteira: os filtros da tabela não mexem nestes números.
 *
 * @param {Object} props
 * @param {Object} props.summary - bloco `summary` de outbox.php
 * @param {boolean} props.late - recorte de atrasadas ligado
 * @param {Function} props.onToggleLate - clique no card "Atrasadas"
 */
function OutboxSummary({ summary, late = false, onToggleLate }) {
  const s = summary || {};
  const pendingLate = s.pending_late || 0;
  const expiredToday = s.expired_today || 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' },
          gap: 2,
        }}
      >
        <StatCard label="Pendentes" value={s.pending ?? 0} />
        <StatCard
          label="Atrasadas (>15 min)"
          value={pendingLate}
          caption={pendingLate > 0 ? 'despachante parado?' : (late ? 'filtro ligado' : 'clique para filtrar')}
          alert={pendingLate > 0}
          onClick={onToggleLate}
        />
        <StatCard label="Em envio" value={s.processing ?? 0} />
        <StatCard label="Adiadas por limite" value={s.deferred ?? 0} />
        <StatCard label="Enviadas hoje" value={s.sent_today ?? 0} />
        <StatCard label="Falhas hoje" value={s.failed_today ?? 0} />
        <StatCard
          label="Canceladas hoje"
          value={s.canceled_today ?? 0}
          caption={expiredToday > 0 ? `(${expiredToday} expiradas)` : null}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Próximo envio: <strong>{formatServerDateTime(s.next_scheduled_at)}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Relógio do servidor: <strong>{formatServerDateTime(s.db_now)}</strong>
        </Typography>
      </Box>
    </Box>
  );
}

export default OutboxSummary;
